import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, Trophy, TrendingUp, TrendingDown, XCircle, Star, Award, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * MonthlySuccessBar
 * Auto-fetches on mount. Shows success rate from the 1st of current month to today.
 * Includes bonus threshold alert.
 */
function MonthlySuccessBar({ user_id }) {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user_id) {
      fetchMonthlyStats();
    }
  }, [user_id]);

  const fetchMonthlyStats = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/driver/monthly-stats`, {
        params: { driver_id: user_id }
      });
      setStats(response.data);
    } catch (err) {
      setError('No se pudieron cargar las estadísticas del mes');
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Bonus tier config ────────────────────────────────────────────────────
  const getBonusTier = (rate) => {
    if (rate >= 88) return {
      tier: 2,
      label: 'Bono 2 Desbloqueado',
      sublabel: '¡Rendimiento excepcional!',
      icon: Trophy,
      gradient: 'from-amber-400 to-yellow-500',
      bg: 'bg-amber-50',
      border: 'border-amber-300',
      text: 'text-amber-700',
      barColor: 'bg-amber-500',
      pulse: true,
    };
    if (rate >= 83) return {
      tier: 1,
      label: 'Bono 1 Desbloqueado',
      sublabel: '¡Buen trabajo, sigue así!',
      icon: Star,
      gradient: 'from-brand-green to-green-500',
      bg: 'bg-green-50',
      border: 'border-green-300',
      text: 'text-green-700',
      barColor: 'bg-brand-green',
      pulse: false,
    };
    if (rate >= 75) return {
      tier: 0,
      label: 'Métricas Normales',
      sublabel: `Llegas a Bono 1 con ${(83 - rate).toFixed(1)}% más`,
      icon: Award,
      gradient: 'from-blue-400 to-blue-500',
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-700',
      barColor: 'bg-blue-500',
      pulse: false,
    };
    return {
      tier: -1,
      label: 'Por debajo del mínimo',
      sublabel: `Necesitas ${(75 - rate).toFixed(1)}% más para métricas normales`,
      icon: AlertTriangle,
      gradient: 'from-red-400 to-red-500',
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-600',
      barColor: 'bg-red-400',
      pulse: false,
    };
  };

  // ─── Loading state ────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-4">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
          <p className="text-sm text-gray-500 font-montserrat">Cargando estadísticas del mes...</p>
        </div>
      </div>
    );
  }

  // ─── Error state ─────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
        <p className="text-sm text-red-600 font-montserrat">{error}</p>
      </div>
    );
  }

  if (!stats) return null;

  const {
    success_rate = 0,
    total_assigned = 0,
    entregados = 0,
    rechazados = 0,
    cancelados = 0,
    rechazados_rate = 0,
    cancelados_rate = 0,
    month_name = '',
    working_days = 0,
  } = stats;

  const bonusTier = getBonusTier(success_rate);
  const BonusIcon = bonusTier.icon;

  // Threshold positions on the bar (%)
  const thresholds = [
    { pct: 75, label: '75%', color: 'bg-blue-400' },
    { pct: 83, label: '83%', color: 'bg-green-500' },
    { pct: 88, label: '88%', color: 'bg-amber-500' },
  ];

  return (
    <div className="mb-4">
      {/* ── Bonus Alert Banner ───────────────────────────────────────────── */}
      <div className={`rounded-xl border p-4 mb-3 ${bonusTier.bg} ${bonusTier.border}`}>
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg bg-gradient-to-br ${bonusTier.gradient} flex-shrink-0`}>
            <BonusIcon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-bold font-montserrat ${bonusTier.text}`}>
              {bonusTier.label}
            </p>
            <p className={`text-xs mt-0.5 ${bonusTier.text} opacity-80`}>
              {bonusTier.sublabel}
            </p>
          </div>
          {/* Big % on the right */}
          <div className="text-right flex-shrink-0">
            <p className={`text-2xl font-bold font-montserrat ${bonusTier.text} leading-none`}>
              {success_rate.toFixed(1)}%
            </p>
            <p className={`text-[10px] ${bonusTier.text} opacity-70`}>este mes</p>
          </div>
        </div>
      </div>

      {/* ── Main Stats Card ──────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-bold text-gray-800 font-montserrat">
              Rendimiento de {month_name}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {total_assigned} paquetes asignados · {working_days} días con actividad
            </p>
          </div>
        </div>

        {/* Progress bar with threshold markers */}
        <div className="mb-4">
          <div className="w-full bg-gray-100 rounded-full h-3 relative overflow-hidden">
            {/* Threshold tick lines */}
            {thresholds.map((t) => (
              <div
                key={t.pct}
                className="absolute top-0 h-full w-0.5 bg-white/70 z-10"
                style={{ left: `${t.pct}%` }}
              />
            ))}
            {/* Filled bar */}
            <div
              className={`h-full rounded-full transition-all duration-1000 ease-out ${bonusTier.barColor}`}
              style={{ width: `${Math.min(success_rate, 100)}%` }}
            />
          </div>

          {/* Threshold labels */}
          <div className="relative h-5 mt-1">
            {thresholds.map((t) => (
              <div
                key={t.pct}
                className="absolute flex flex-col items-center"
                style={{ left: `${t.pct}%`, transform: 'translateX(-50%)' }}
              >
                <div className={`w-1.5 h-1.5 rounded-full ${t.color}`} />
                <span className="text-[9px] text-gray-400 font-montserrat mt-0.5">{t.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Breakdown */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-green-50 rounded-lg p-3 text-center border border-green-100">
            <TrendingUp className="w-4 h-4 text-green-600 mx-auto mb-1" />
            <p className="text-lg font-bold text-green-700 font-montserrat">{entregados}</p>
            <p className="text-[10px] text-green-600">Entregados</p>
            <p className="text-[10px] text-green-500 font-bold">{success_rate.toFixed(1)}%</p>
          </div>

          <div className="bg-red-50 rounded-lg p-3 text-center border border-red-100">
            <TrendingDown className="w-4 h-4 text-red-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-red-600 font-montserrat">{rechazados}</p>
            <p className="text-[10px] text-red-500">Rechazados</p>
            <p className="text-[10px] text-red-400 font-bold">{rechazados_rate.toFixed(1)}%</p>
          </div>

          <div className="bg-orange-50 rounded-lg p-3 text-center border border-orange-100">
            <XCircle className="w-4 h-4 text-orange-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-orange-600 font-montserrat">{cancelados}</p>
            <p className="text-[10px] text-orange-500">Cancelados</p>
            <p className="text-[10px] text-orange-400 font-bold">{cancelados_rate.toFixed(1)}%</p>
          </div>
        </div>

        {/* Bonus thresholds legend */}
        <div className="mt-4 pt-3 border-t border-gray-100">
          <p className="text-[10px] text-gray-400 font-montserrat mb-2 text-center uppercase tracking-wide">
            Niveles de bono
          </p>
          <div className="flex justify-between text-[10px] font-montserrat">
            <span className="text-red-400">{'<75%'} Bajo</span>
            <span className="text-blue-500">75–82% Normal</span>
            <span className="text-green-600">83–87% Bono 1</span>
            <span className="text-amber-600">88%+ Bono 2</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MonthlySuccessBar;